import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./codeBlock.html";
import hljs from 'highlight.js/lib/core';
import bash from 'highlight.js/lib/languages/bash';
import css from 'highlight.js/lib/languages/css';
import java from 'highlight.js/lib/languages/java';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import python from 'highlight.js/lib/languages/python';
import sql from 'highlight.js/lib/languages/sql';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';
import yaml from 'highlight.js/lib/languages/yaml';
import { selectSharedLabels, WireI18n } from '~/modules/stores/i18nStore';
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('css', css);
hljs.registerLanguage('java', java);
hljs.registerLanguage('apex', java);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('json', json);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('md', markdown);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('soql', sql);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('yaml', yaml);
class CodeBlock extends LightningElement {
  constructor(...args) {
    super(...args);
    this._code = '';
    this.cachedHighlightKey = '';
    this.cachedHighlightedHtml = '';
    this.renderedHtml = '';
    /**
     * Optional accessible name for the scrollable code region. Consumers that already know what the
     * code is (e.g. a file preview that knows the file name) should pass it so the focus stop is
     * self-describing; the consumer is responsible for localizing whatever it passes. When omitted we
     * fall back to the localized shared "Code sample" label rather than hardcoding English, because
     * this deployment also serves the AppExchange JP and AgentExchange JP experiences.
     */
    this.label = void 0;
    this.sharedLabels = void 0;
  }
  get code() {
    return this._code;
  }
  set code(v) {
    this._code = v ?? '';
    this.updateContent();
  }
  get language() {
    return this._language;
  }
  set language(value) {
    this._language = value;
    this.updateContent();
  }
  get codeRegionLabel() {
    return this.label || this.sharedLabels?.codeSample || '';
  }
  get lineNumbers() {
    const lines = this._code ? this._code.split('\n') : [];
    if (lines.length > 1 && lines[lines.length - 1] === '') {
      lines.pop();
    }
    const lineCount = lines.length;
    return Array.from({
      length: lineCount
    }, (_, index) => ({
      key: index,
      number: index + 1
    }));
  }

  // hljs.highlight() throws for a language it doesn't recognize, so check getLanguage()
  // first and fall back to highlightAuto() rather than relying on try/catch for control flow.
  // The returned HTML is safe to inject: hljs's emitter escapes all source text and only
  // adds its own <span class="hljs-*"> wrapper markup (see highlight.js/lib/core.js escapeHTML).
  get highlightedHtml() {
    const language = this.language;
    const cacheKey = `${language ?? ''}\u0000${this._code}`;
    if (cacheKey === this.cachedHighlightKey) {
      return this.cachedHighlightedHtml;
    }
    this.cachedHighlightKey = cacheKey;
    if (language && hljs.getLanguage(language)) {
      this.cachedHighlightedHtml = hljs.highlight(this._code, {
        language
      }).value;
    } else {
      this.cachedHighlightedHtml = hljs.highlightAuto(this._code).value;
    }
    return this.cachedHighlightedHtml;
  }
  updateContent() {
    const codeEl = this.template?.querySelector('.code');
    const highlightedHtml = this.highlightedHtml;
    if (codeEl && highlightedHtml !== this.renderedHtml) {
      codeEl.innerHTML = highlightedHtml;
      this.renderedHtml = highlightedHtml;
    }
  }
  renderedCallback() {
    this.updateContent();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(CodeBlock, {
  publicProps: {
    code: {
      config: 3
    },
    language: {
      config: 3
    },
    label: {
      config: 0
    }
  },
  wire: {
    sharedLabels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSharedLabels
        };
      }
    }
  },
  fields: ["_code", "cachedHighlightKey", "cachedHighlightedHtml", "renderedHtml"]
});
const __lwc_component_class_internal = _registerComponent(CodeBlock, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;